import { css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import '../icon/icon.element';
import { Order } from './table.utils';

@customElement('cds-table-header-cell')
export class TableHeaderCellElement extends LitElement {
  static styles = css`
    :host {
      align-items: center;
      color: var(--token-component-table-header-outlined-text, #343438, #ffffff);
      display: flex;
      flex: 1 0 0;
      font-family: var(--token-global-font-font-family-brand, 'Nunito Sans');
      font-size: var(--token-global-font-font-size-xs, 0.875rem); /* 14px */
      font-style: normal;
      font-weight: var(--token-global-font-font-weight-bold, 700);
      gap: var(--token-global-spacing-s8, 8px);
      line-height: var(--token-global-font-line-height-xxxs, 1rem); /* 16px */;
      padding: var(--token-global-spacing-s12, 12px) var(--token-global-spacing-s16, 16px) var(--token-global-spacing-s12, 12px) var(--token-global-spacing-s12, 12px);
      min-width: 0;
    }

    .cds-table-row-checkbox,
    .cds-table-head-sort-icon {
      display: none;
    }

    .cds-table-header-cell-content {
      display: flex;
      align-items: center;
      gap: var(--token-global-spacing-s8, 8px);
      min-width: 0;
    }

    :host([selectable]) {
      .cds-table-row-checkbox {
        display: inline-block;
      }
    }

    :host([sortable]) {
      cursor: pointer;
    }

    :host([sorted='asc']) .cds-table-head-sort-icon,
    :host([sorted='desc']) .cds-table-head-sort-icon,
    :host([sortable]):host(:hover) .cds-table-head-sort-icon {
      cursor: pointer;
      display: block;
    }

    :host([sorted='desc']) .cds-table-head-sort-icon {
      transform: rotate(180deg);
    }
  `;

  @property({ type: String, reflect: true })
  role = 'columnheader';

  @property({ type: String, reflect: true })
  scope = 'col';

  @property({ type: Boolean, reflect: true })
  selectable = false;

  @property({ type: Boolean, reflect: true })
  selected = false;

  @property({ type: Boolean, reflect: true })
  sortable = false;

  @property({ type: String, reflect: true })
  sorted: Order | undefined;

  private observer: MutationObserver | null = null;

  connectedCallback() {
    super.connectedCallback();
    this.setupParentObserver();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  render() {
    return html`
      ${this.selectable
        ? html`
          <cds-checkbox
            class="cds-table-row-checkbox"
            .checked=${this.selected}
            @change=${this.handleSelect}
          ></cds-checkbox>
        `
        : ''}
      <div class="cds-table-header-cell-content" @click=${this.handleSort}>
        <slot></slot>
        <cds-icon
          class="cds-table-head-sort-icon"
          icon="arrow-up"
          size="s"
        ></cds-icon>
      </div>
    `;
  }

  private handleSelect(event: Event) {
    event.preventDefault();
    this.selected = !this.selected;
    this.dispatchEvent(
      new CustomEvent('select', {
        detail: this,
        bubbles: true,
        composed: true,
      }),
    );
  }

  private handleSort(event: Event) {
    event.preventDefault();
    if (!this.sortable) {
      return;
    }
    if (this.sorted === 'asc') {
      this.sorted = 'desc';
    } else {
      this.sorted = 'asc';
    }
    this.dispatchEvent(new CustomEvent('sort', { detail: this.sorted }));
  }

  private setupParentObserver() {
    const parentRow = this.closest('cds-table-row');
    if (!parentRow) return;

    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'selected') {
          this.selected = parentRow.hasAttribute('selected');
        }
      });
    });

    this.observer.observe(parentRow, {
      attributes: true,
      attributeFilter: ['selected']
    });

    // Set initial state
    this.selected = parentRow.hasAttribute('selected');
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cds-table-header-cell': TableHeaderCellElement;
  }
}
